import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Wrench, MoreVertical, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export default function Prestations() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const schema = [
    { name: "nom", label: "Nom de la prestation", required: true, col: 2 },
    { name: "prix", label: "Prix unitaire (FCFA)", type: "number" },
    { name: "unite", label: "Unité", type: "select", options: settings.units || [] },
    { name: "tva", label: "TVA (%)", type: "number" },
    { name: "actif", label: "Statut", type: "select", options: [{ value: true, label: "Actif" }, { value: false, label: "Inactif" }] },
    { name: "description", label: "Description", type: "textarea" },
    { name: "infos", label: "Informations complémentaires", type: "textarea" },
  ];

  const save = async (form) => {
    const payload = { ...form, actif: form.actif === true || form.actif === "true" };
    try {
      if (editing) { await api.put(`/services/${editing.id}`, payload); toast.success("Prestation mise à jour"); }
      else { await api.post("/services", payload); toast.success("Prestation créée"); }
      load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette prestation ?")) return; await api.delete(`/services/${id}`); toast.success("Supprimé"); load(); };

  const filtered = (items || []).filter((s) => !q || `${s.nom} ${s.description}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Prestations" subtitle="Catalogue de prestations réutilisables dans les devis et factures">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-service" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle prestation</Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="pl-9" data-testid="service-filter" />
      </div>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={Wrench} title="Aucune prestation" subtitle="Créez vos prestations pour les réutiliser dans les documents commerciaux." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Réf.</TableHead><TableHead>Nom</TableHead><TableHead>Prix</TableHead>
              <TableHead>Unité</TableHead><TableHead>TVA</TableHead><TableHead>Statut</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`service-row-${s.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs text-slate-500">{s.reference}</TableCell>
                  <TableCell className="font-semibold">{s.nom}</TableCell>
                  <TableCell className="font-mono text-sm">{(s.prix || 0).toLocaleString()} F</TableCell>
                  <TableCell className="text-slate-600">{s.unite || "—"}</TableCell>
                  <TableCell className="text-slate-600">{s.tva ?? 0}%</TableCell>
                  <TableCell><StatusBadge value={s.actif ? "Actif" : "Inactif"} /></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`service-menu-${s.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(s); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(s.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier la prestation" : "Nouvelle prestation"}
        schema={schema} initial={editing ? { ...editing, actif: editing.actif } : { actif: true, unite: "u", tva: 18 }} onSubmit={save} />
    </div>
  );
}
